import { Group, Stack, Text, Title, UnstyledButton } from '@mantine/core';
import { Card } from '@/components/ui';
import { formatChangePct, formatMarketCap, formatPrice } from '@/lib/format';
import type { TickerRow } from '@/hooks/useTickers';
import { BuzzBadge } from '@/components/shared/BuzzBadge';

interface StockCardProps {
  ticker: TickerRow;
  onSelect?: (ticker: TickerRow) => void;
}

function changeColour(value: number | null): string {
  if (value === null || value === 0) return 'inherit';
  return value > 0 ? 'teal' : 'red';
}

export function StockCard({ ticker, onSelect }: StockCardProps) {
  return (
    <Card p={0}>
      <UnstyledButton
        onClick={() => onSelect?.(ticker)}
        style={{ display: 'block', width: '100%', padding: 'var(--mantine-spacing-md)', textAlign: 'left' }}
        aria-label={`View details for ${ticker.symbol}`}
      >
      <Stack gap="xs">
        <Group justify="space-between" wrap="nowrap" align="flex-start">
          <Stack gap={0}>
            <Title order={3} size="h4">
              {ticker.symbol}
            </Title>
            <Text size="sm" c="dimmed" lineClamp={1}>
              {ticker.name}
            </Text>
          </Stack>
          <BuzzBadge score={ticker.buzz_score} />
        </Group>
        <Group justify="space-between" wrap="nowrap">
          <Stack gap={0}>
            <Text size="xs" c="dimmed">Price</Text>
            <Text fw={600} size="lg">{formatPrice(ticker.last_price)}</Text>
          </Stack>
          <Stack gap={0} align="flex-end">
            <Text size="xs" c="dimmed">Change</Text>
            <Text c={changeColour(ticker.last_price_change_pct)} fw={500}>
              {formatChangePct(ticker.last_price_change_pct)}
            </Text>
          </Stack>
        </Group>
        <Stack gap={0}>
          <Text size="xs" c="dimmed">Market cap</Text>
          <Text size="sm">
            {formatMarketCap(ticker.market_cap)}
            {ticker.industry ? ` · ${ticker.industry}` : ''}
          </Text>
        </Stack>
      </Stack>
      </UnstyledButton>
    </Card>
  );
}
